import type { AccountInfo, RedirectRequest, SilentRequest } from '@azure/msal-browser'
import { authService } from './services/authService'

const RelayStorageKey = 'tenant-secrets-token-relay'
const RelayCallbackPath = '/token'
const MinimumAllowedRelayPort = 1024
const MaximumAllowedRelayPort = 65535

interface RelayState {
  callbackUrl: string
  apiScope: string
  state: string
}

interface RelayPayload {
  apiAccessToken?: string
  apiExpiresAt?: string
  state?: string
  error?: string
}

export async function maybeRunTokenRelay(): Promise<boolean> {
  if (!import.meta.env.DEV) {
    return false
  }

  const currentUrl = new URL(window.location.href)
  const callbackUrl = currentUrl.searchParams.get('tokenRelayCallback')
  const apiScope = currentUrl.searchParams.get('tokenRelayApiScope')
  const state = currentUrl.searchParams.get('tokenRelayState')

  if (callbackUrl || apiScope) {
    currentUrl.searchParams.delete('tokenRelayCallback')
    currentUrl.searchParams.delete('tokenRelayApiScope')
    currentUrl.searchParams.delete('tokenRelayState')
    window.history.replaceState({}, document.title, currentUrl.toString())

    const relayState = createRelayState(callbackUrl, apiScope, state)
    if (!relayState) {
      clearRelayState()
      renderRelayStatus('Invalid token relay request', 'The requested callback URL is not allowed. Token relay only supports loopback HTTP callbacks on localhost using the /token endpoint.')
      return true
    }

    saveRelayState(relayState)
  }

  const relayState = loadRelayState()
  if (!relayState) {
    return false
  }

  renderRelayStatus('Tenant secrets token relay', 'Authenticating in the browser and relaying tokens to the console...')

  try {
    await authService.initialize()

    const account = authService.getAccount()
    if (!account) {
      await authService.loginRedirect()
      return true
    }

    const apiToken = await acquireApiToken(account, relayState.apiScope)

    await postRelayPayload(relayState.callbackUrl, {
      apiAccessToken: apiToken.accessToken,
      apiExpiresAt: apiToken.expiresOn?.toISOString(),
      state: relayState.state,
    })

    clearRelayState()
    renderRelayStatus('Authentication complete', 'You can return to the console application. Feel free to close this browser tab.')
    return true
  } catch (error) {
    const message = formatError(error)

    try {
      await postRelayPayload(relayState.callbackUrl, { error: message, state: relayState.state })
    } catch {
      // Best-effort callback reporting when the console listener is unreachable.
    } finally {
      clearRelayState()
    }

    renderRelayStatus('Authentication failed', message)
    return true
  }
}

async function acquireApiToken(account: AccountInfo, scope: string) {
  const silentRequest: SilentRequest = {
    scopes: [scope],
    account,
  }

  try {
    return await authService.getInstance().acquireTokenSilent(silentRequest)
  } catch {
    return await authService.getInstance().acquireTokenPopup(createInteractiveRequest(scope, account))
  }
}

function createInteractiveRequest(scope: string, account?: AccountInfo): RedirectRequest {
  return {
    scopes: [scope],
    prompt: 'select_account',
    account,
  }
}

async function postRelayPayload(callbackUrl: string, payload: RelayPayload): Promise<void> {
  const response = await fetch(callbackUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  })

  if (!response.ok) {
    throw new Error(`Relay callback failed with HTTP ${response.status}`)
  }
}

function saveRelayState(relayState: RelayState): void {
  sessionStorage.setItem(RelayStorageKey, JSON.stringify(relayState))
}

function loadRelayState(): RelayState | null {
  const rawValue = sessionStorage.getItem(RelayStorageKey)
  if (!rawValue) {
    return null
  }

  try {
    const parsed = JSON.parse(rawValue) as Partial<RelayState>
    const relayState = createRelayState(
      typeof parsed.callbackUrl === 'string' ? parsed.callbackUrl : null,
      typeof parsed.apiScope === 'string' ? parsed.apiScope : null,
      typeof parsed.state === 'string' ? parsed.state : null,
    )
    if (!relayState) {
      clearRelayState()
    }
    return relayState
  } catch {
    clearRelayState()
    return null
  }
}

function createRelayState(callbackUrl: string | null, apiScope: string | null, state: string | null): RelayState | null {
  const normalizedScope = apiScope?.trim()
  const normalizedCallbackUrl = callbackUrl?.trim()
  const normalizedState = state?.trim()
  if (!normalizedScope || !normalizedCallbackUrl || !normalizedState) {
    return null
  }

  const validatedCallbackUrl = validateRelayCallbackUrl(normalizedCallbackUrl)
  if (!validatedCallbackUrl) {
    return null
  }

  return {
    callbackUrl: validatedCallbackUrl.toString(),
    apiScope: normalizedScope,
    state: normalizedState,
  }
}

function validateRelayCallbackUrl(callbackUrl: string): URL | null {
  let parsedUrl: URL
  try {
    parsedUrl = new URL(callbackUrl)
  } catch {
    return null
  }

  if (parsedUrl.protocol !== 'http:') {
    return null
  }

  if (!isLoopbackHost(parsedUrl.hostname)) {
    return null
  }

  if (parsedUrl.pathname !== RelayCallbackPath) {
    return null
  }

  if (parsedUrl.username || parsedUrl.password || parsedUrl.search || parsedUrl.hash) {
    return null
  }

  const port = Number(parsedUrl.port)
  if (!Number.isInteger(port) || port < MinimumAllowedRelayPort || port > MaximumAllowedRelayPort) {
    return null
  }

  return parsedUrl
}

function isLoopbackHost(hostname: string): boolean {
  const normalizedHost = hostname.toLowerCase()
  return normalizedHost === 'localhost'
    || normalizedHost === '127.0.0.1'
    || normalizedHost === '::1'
    || normalizedHost === '[::1]'
}

function clearRelayState(): void {
  sessionStorage.removeItem(RelayStorageKey)
}

function renderRelayStatus(title: string, description: string): void {
  document.body.innerHTML = `
    <main style="font-family: Segoe UI, sans-serif; max-width: 42rem; margin: 4rem auto; padding: 0 1.5rem; line-height: 1.5;">
      <h1 style="margin-bottom: 0.75rem; font-size: 1.75rem;">${escapeHtml(title)}</h1>
      <p style="color: #444;">${escapeHtml(description)}</p>
    </main>
  `
}

function formatError(error: unknown): string {
  if (error instanceof Error) {
    return error.message
  }

  return String(error)
}

function escapeHtml(value: string): string {
  return value
    .split('&').join('&amp;')
    .split('<').join('&lt;')
    .split('>').join('&gt;')
    .split('"').join('&quot;')
    .split("'").join('&#39;')
}
